import filter from 'lodash/filter'
import isNumber from 'lodash/isNumber'
import map from 'lodash/map'
import min from 'lodash/min'
import type { ScannedPage } from '@ztechapps/quickscore.contract'

/**
 * How far the AI's read of a sheet can be trusted. A band, not the raw number, is what support
 * acts on — "can I believe this score" is the question, and a bare 0.87 does not answer it.
 */
export enum ConfidenceBandEnum {
  High = 'High',
  Medium = 'Medium',
  Low = 'Low',
  Unknown = 'Unknown',
}

/** Page confidence is a 0-1 fraction, so both boundaries are fractions, never percentages. */
export const HIGH_CONFIDENCE_THRESHOLD = 0.9
export const MEDIUM_CONFIDENCE_THRESHOLD = 0.7

export type ConfidenceBand = {
  band: ConfidenceBandEnum
  /** The confidence the band was derived from. Absent when no page carried one. */
  confidence?: number
}

/**
 * Bands a sheet by its WEAKEST page: a sheet is only as readable as its worst photograph, and one
 * badly-lit page is exactly what support opens the panel to find — an average would hide it behind
 * the good pages.
 *
 * `aiConfidence` is optional per page, so pages carrying none are dropped rather than read as zero.
 * A sheet where no page carries one bands as `Unknown`, never as `Low`: nothing was measured, and
 * reporting a poor read that was never taken would send support chasing a scan that is fine.
 */
export const deriveConfidenceBand = (pages?: ScannedPage[]): ConfidenceBand => {
  const confidence = min(filter(map(pages, 'aiConfidence'), isNumber))

  if (!isNumber(confidence)) return { band: ConfidenceBandEnum.Unknown }
  if (confidence >= HIGH_CONFIDENCE_THRESHOLD) return { band: ConfidenceBandEnum.High, confidence }
  if (confidence >= MEDIUM_CONFIDENCE_THRESHOLD) return { band: ConfidenceBandEnum.Medium, confidence }

  return { band: ConfidenceBandEnum.Low, confidence }
}
